import { useTheme } from '@/contexts/ThemeContext';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const themes = [
    { value: 'light' as const, label: 'Claro', icon: Sun, description: 'Tema claro' },
    { value: 'dark' as const, label: 'Escuro', icon: Moon, description: 'Tema escuro' },
    { value: 'system' as const, label: 'Sistema', icon: Monitor, description: 'Seguir sistema' },
  ];

  const currentTheme = themes.find((t) => t.value === theme);
  const CurrentIcon = currentTheme?.icon || Sun;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center w-10 h-10 rounded-sm transition-all duration-200 bg-card hover:bg-accent text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
        title="Alternar tema"
      >
        <CurrentIcon className="w-5 h-5" />
      </button>
      {isOpen && (
        <div className="absolute bottom-full mb-2 right-0 w-48 rounded-sm shadow-lg border bg-card border-border overflow-hidden z-50 animate-slide-down">
          <div className="py-1">
            {themes.map((themeOption) => {
              const Icon = themeOption.icon;
              const isActive = theme === themeOption.value;
              return (
                <button
                  key={themeOption.value}
                  onClick={() => { setTheme(themeOption.value); setIsOpen(false); }}
                  className={isActive ? 'w-full px-4 py-3 flex items-center gap-3 bg-primary text-primary-foreground' : 'w-full px-4 py-3 flex items-center gap-3 text-muted-foreground hover:bg-accent hover:text-foreground'}
                >
                  <div className={isActive ? 'flex items-center justify-center w-8 h-8 rounded-sm bg-primary/20' : 'flex items-center justify-center w-8 h-8 rounded-sm bg-card'}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium">{themeOption.label}</div>
                    <div className={isActive ? 'text-xs text-primary-foreground/70' : 'text-xs text-muted-foreground'}>{themeOption.description}</div>
                  </div>
                  {isActive && <div className="w-2 h-2 rounded-full bg-primary-foreground" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
